import { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/api";
import Shell from "@/components/Shell";
import { Spinner, Overline, Stat } from "@/components/Brand";
import { toast } from "sonner";
import { useScrollLock } from "@/hooks/useScrollLock";
import { ShieldCheck, Store, Users2, UserCog, BookOpen, Plus, Upload, X, Sparkles, ToggleLeft, ToggleRight, Settings2, Palette } from "lucide-react";

const nav = [{ key: "console", to: "/superadmin", label: "Console", icon: ShieldCheck }];
const CATS = ["Gold", "Diamond", "Polki", "Kundan", "Silver", "Gemstones", "Couture", "Temple"];

export default function SuperAdmin() {
  const [tab, setTab] = useState("exhibitors");
  const [ov, setOv] = useState(null);
  const [exhibitors, setExhibitors] = useState(null);
  const [visitors, setVisitors] = useState(null);
  const [admins, setAdmins] = useState(null);
  const [branding, setBranding] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [newEx, setNewEx] = useState(null);
  const [newAdmin, setNewAdmin] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const [tradeClasses, setTradeClasses] = useState([]);
  const exCsvRef = useRef();
  const viCsvRef = useRef();

  const loadOv = () => api.get("/superadmin/overview").then(({ data }) => setOv(data));
  const loadEx = () => api.get("/superadmin/exhibitors").then(({ data }) => setExhibitors(data));
  const loadVi = () => api.get("/superadmin/visitors").then(({ data }) => setVisitors(data));
  const loadAd = () => api.get("/superadmin/admins").then(({ data }) => setAdmins(data));

  const saveTrade = async () => {
    if (!activeEvent) return;
    try { await api.put(`/superadmin/events/${activeEvent.id}/trade-classifications`, { trade_classifications: tradeClasses }); toast.success("Trade classes saved"); }
    catch { toast.error("Failed to save"); }
  };

  useEffect(() => {
    loadOv(); loadEx();
    api.get("/superadmin/branding").then(({ data }) => setBranding(data));
    api.get("/integrations/status").then(({ data }) => setIntegrations(data));
    api.get("/events/active").then(({ data }) => { setActiveEvent(data); setTradeClasses(data.trade_classifications || []); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (tab === "visitors" && !visitors) loadVi();
    if (tab === "admins" && !admins) loadAd();
    // eslint-disable-next-line
  }, [tab]);

  const createEx = async () => {
    try {
      await api.post("/superadmin/exhibitors", newEx);
      toast.success("Exhibitor created");
      setNewEx(null); loadEx(); loadOv();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const setCredits = async (id, val) => {
    await api.put(`/superadmin/exhibitors/${id}/credits`, { ai_credits: Math.max(0, parseInt(val) || 0) });
    loadEx(); toast.success("Credits updated");
  };
  const toggleFeature = async (ex, key) => {
    const features = { ...ex.features, [key]: !ex.features?.[key] };
    await api.put(`/superadmin/exhibitors/${ex.id}/features`, { features });
    loadEx();
  };
  const importCsv = async (ref, url, reload) => {
    const file = ref.current?.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(url, fd);
      toast.success(`Imported ${data.created} records`);
      reload(); loadOv();
    } catch (err) { toast.error(apiErr(err)); }
    finally { ref.current.value = ""; }
  };
  const createAdmin = async () => {
    try { await api.post("/superadmin/admins", newAdmin); toast.success("Admin created"); setNewAdmin(null); loadAd(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const saveBranding = async () => {
    try { const { data } = await api.put("/superadmin/branding", branding); setBranding(data); toast.success("Branding saved"); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const TABS = [
    { k: "exhibitors", label: "Exhibitors" }, { k: "visitors", label: "Visitors" },
    { k: "admins", label: "Admins" }, { k: "settings", label: "Settings" },
  ];

  return (
    <Shell nav={nav}>
      <Overline>Platform Super Admin</Overline>
      <h1 className="font-display text-3xl sm:text-4xl gold-text mt-2">Operations Console</h1>

      {ov && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mt-6">
          <Stat testid="super-ex" label="Exhibitors" value={ov.exhibitors} icon={Store} />
          <Stat testid="super-vi" label="Visitors" value={ov.visitors} icon={Users2} />
          <Stat testid="super-staff" label="Staff Users" value={ov.staff_users} icon={UserCog} />
          <Stat testid="super-admins" label="Admins" value={ov.admins} icon={ShieldCheck} />
          <Stat testid="super-leads" label="Leads" value={ov.leads} icon={Users2} />
          <Stat testid="super-cats" label="Catalogues" value={ov.catalogues} icon={BookOpen} />
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto mt-6 pb-1" style={{ scrollbarWidth: "none" }}>
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`super-tab-${t.k}`} className={`whitespace-nowrap text-sm px-4 py-2 rounded-full border transition ${tab === t.k ? "btn-gold border-transparent" : "glass text-slate-300 border-amber-500/20"}`}>{t.label}</button>
        ))}
      </div>

      {tab === "exhibitors" && (
        <div className="mt-6">
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => setNewEx({ name: "", email: "", password: "jjs1234", hall: "", booth: "", city: "", description: "", categories: [] })} className="btn-gold px-4 py-2.5 rounded-xl text-sm flex items-center gap-2" data-testid="add-exhibitor-button"><Plus size={15} /> Add Exhibitor</button>
            <input ref={exCsvRef} type="file" accept=".csv" className="hidden" onChange={() => importCsv(exCsvRef, "/superadmin/import/exhibitors", loadEx)} data-testid="import-exhibitors-input" />
            <button onClick={() => exCsvRef.current?.click()} className="glass px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 text-slate-200" data-testid="import-exhibitors-button"><Upload size={15} /> Import CSV</button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">CSV columns: name, email, password, hall, booth, city, categories (pipe|separated), description</p>

          {!exhibitors ? <Spinner /> : (
            <div className="space-y-3 mt-4">
              {exhibitors.map((ex) => (
                <div key={ex.id} className="glass rounded-2xl p-4" data-testid={`super-exhibitor-${ex.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-slate-100 font-medium">{ex.name} {ex.activated ? <span className="text-[10px] text-emerald-300">● live</span> : <span className="text-[10px] text-amber-300">○ pending</span>}</div>
                      <div className="text-xs text-slate-400">{ex.hall} · {ex.booth} · {ex.login_email}</div>
                      <div className="font-mono-j text-[11px] text-amber-300/80 mt-1">{ex.qr_code}</div>
                    </div>
                    <div className="text-right">
                      <label className="text-[10px] uppercase tracking-wide text-slate-500 block">AI Credits</label>
                      <input defaultValue={ex.ai_credits} onBlur={(e) => setCredits(ex.id, e.target.value)} type="number" className="w-16 bg-black/30 gold-border rounded-lg px-2 py-1 text-sm text-slate-100 text-center mt-1 focus:outline-none" data-testid={`superadmin-credit-allocation-input-${ex.id}`} />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {[["ai_catalogue", "AI Catalogue"], ["ocr_cards", "Card OCR"], ["auto_followup", "Auto Follow-up"]].map(([k, label]) => (
                      <button key={k} onClick={() => toggleFeature(ex, k)} data-testid={`feature-${k}-${ex.id}`} className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition ${ex.features?.[k] ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "glass text-slate-400 border-slate-500/30"}`}>
                        {ex.features?.[k] ? <ToggleRight size={14} /> : <ToggleLeft size={14} />} {label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "visitors" && (
        <div className="mt-6">
          <input ref={viCsvRef} type="file" accept=".csv" className="hidden" onChange={() => importCsv(viCsvRef, "/superadmin/import/visitors", loadVi)} data-testid="import-visitors-input" />
          <button onClick={() => viCsvRef.current?.click()} className="glass px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 text-slate-200" data-testid="import-visitors-button"><Upload size={15} /> Import Badge CSV</button>
          <p className="text-[11px] text-slate-500 mt-2">CSV columns: phone, badge_id, name, classification</p>
          {!visitors ? <Spinner /> : (
            <div className="space-y-2 mt-4">
              {visitors.map((v) => (
                <div key={v.id} className="glass rounded-xl p-3 flex items-center justify-between" data-testid={`super-visitor-${v.id}`}>
                  <div><div className="text-slate-100 text-sm font-medium">{v.name}</div><div className="text-xs text-slate-400 font-mono-j">{v.phone}</div></div>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 capitalize">{v.classification}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "admins" && (
        <div className="mt-6">
          <button onClick={() => setNewAdmin({ name: "", email: "", password: "", role: "jjs_admin" })} className="btn-gold px-4 py-2.5 rounded-xl text-sm flex items-center gap-2" data-testid="add-admin-button"><Plus size={15} /> Add Admin</button>
          {!admins ? <Spinner /> : (
            <div className="space-y-2 mt-4">
              {admins.map((a) => (
                <div key={a.id} className="glass rounded-xl p-3 flex items-center justify-between" data-testid={`super-admin-${a.id}`}>
                  <div><div className="text-slate-100 text-sm font-medium">{a.name}</div><div className="text-xs text-slate-400">{a.email}</div></div>
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300">{a.role.replace("_", " ")}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "settings" && branding && (
        <div className="mt-6 space-y-4">
          <div className="glass rounded-2xl p-5">
            <div className="flex items-center gap-2 text-slate-200 font-medium"><Palette size={16} className="text-amber-400" /> Branding</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
              <input value={branding.app_name || ""} onChange={(e) => setBranding({ ...branding, app_name: e.target.value })} placeholder="App name" className="bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="branding-app-name" />
              <input value={branding.event_name || ""} onChange={(e) => setBranding({ ...branding, event_name: e.target.value })} placeholder="Event name" className="bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="branding-event-name" />
            </div>
            <button onClick={saveBranding} className="btn-gold px-4 py-2.5 rounded-xl text-sm mt-4" data-testid="branding-save">Save Branding</button>
          </div>

          <div className="glass rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-slate-200 font-medium"><Settings2 size={16} className="text-amber-400" /> WhatsApp Delivery</div>
              <button onClick={() => setBranding({ ...branding, whatsapp_enabled: !branding.whatsapp_enabled })} data-testid="whatsapp-toggle" className={`text-xs px-3 py-1.5 rounded-full border flex items-center gap-1.5 ${branding.whatsapp_enabled ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "glass text-slate-400 border-slate-500/30"}`}>
                {branding.whatsapp_enabled ? <ToggleRight size={14} /> : <ToggleLeft size={14} />} {branding.whatsapp_enabled ? "Enabled" : "Disabled"}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">No provider is connected yet. Enabling only flags readiness — messages are not sent until a WhatsApp provider is integrated.</p>
            <button onClick={saveBranding} className="btn-gold px-4 py-2.5 rounded-xl text-sm mt-3" data-testid="whatsapp-save">Save</button>
          </div>

          {integrations && (
            <div className="glass rounded-2xl p-5">
              <div className="flex items-center gap-2 text-slate-200 font-medium"><Sparkles size={16} className="text-amber-400" /> Trade-Visitor Access (catalogues)</div>
              <p className="text-xs text-slate-400 mt-2">Select which visitor classifications count as "trade" for trade-only catalogue links (active edition).</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {["buyer", "retailer", "trader", "vendor", "general"].map((cls) => {
                  const on = (tradeClasses || []).includes(cls);
                  return (
                    <button key={cls} onClick={() => setTradeClasses(on ? tradeClasses.filter((x) => x !== cls) : [...(tradeClasses || []), cls])} data-testid={`trade-class-${cls}`} className={`text-xs px-3 py-1.5 rounded-full border capitalize ${on ? "btn-gold border-transparent" : "glass text-slate-400 border-slate-500/30"}`}>{cls}</button>
                  );
                })}
              </div>
              <button onClick={saveTrade} className="btn-gold px-4 py-2 rounded-xl text-sm mt-3" data-testid="trade-save">Save Trade Classes</button>
            </div>
          )}

          {integrations && (
            <div className="glass rounded-2xl p-5">
              <div className="flex items-center gap-2 text-slate-200 font-medium"><Sparkles size={16} className="text-amber-400" /> Integration Status</div>
              <div className="space-y-2 mt-3">
                {Object.entries(integrations).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm" data-testid={`integration-${k}`}>
                    <span className="text-slate-300 capitalize">{k.replace(/_/g, " ")}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${v.status === "connected" ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-500/15 text-slate-400"}`}>{v.status.replace("_", " ")}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create exhibitor modal */}
      {newEx && (
        <Modal onClose={() => setNewEx(null)} title="New Exhibitor" testid="new-exhibitor-modal">
          <div className="grid grid-cols-1 gap-3">
            <Inp v={newEx.name} set={(x) => setNewEx({ ...newEx, name: x })} ph="Exhibitor name" t="new-ex-name" />
            <Inp v={newEx.email} set={(x) => setNewEx({ ...newEx, email: x })} ph="Login email" t="new-ex-email" />
            <Inp v={newEx.password} set={(x) => setNewEx({ ...newEx, password: x })} ph="Temp password" t="new-ex-password" />
            <div className="grid grid-cols-2 gap-3">
              <Inp v={newEx.hall} set={(x) => setNewEx({ ...newEx, hall: x })} ph="Hall" t="new-ex-hall" />
              <Inp v={newEx.booth} set={(x) => setNewEx({ ...newEx, booth: x })} ph="Booth" t="new-ex-booth" />
            </div>
            <Inp v={newEx.city} set={(x) => setNewEx({ ...newEx, city: x })} ph="City" t="new-ex-city" />
            <div className="flex flex-wrap gap-1.5">
              {CATS.map((c) => (
                <button key={c} onClick={() => setNewEx({ ...newEx, categories: newEx.categories.includes(c) ? newEx.categories.filter((x) => x !== c) : [...newEx.categories, c] })} className={`text-xs px-2.5 py-1 rounded-full border ${newEx.categories.includes(c) ? "btn-gold border-transparent" : "glass text-slate-300 border-amber-500/20"}`}>{c}</button>
              ))}
            </div>
            <button onClick={createEx} className="btn-gold py-3 rounded-xl" data-testid="new-ex-submit">Create</button>
          </div>
        </Modal>
      )}
      {newAdmin && (
        <Modal onClose={() => setNewAdmin(null)} title="New Admin" testid="new-admin-modal">
          <div className="grid grid-cols-1 gap-3">
            <Inp v={newAdmin.name} set={(x) => setNewAdmin({ ...newAdmin, name: x })} ph="Name" t="new-admin-name" />
            <Inp v={newAdmin.email} set={(x) => setNewAdmin({ ...newAdmin, email: x })} ph="Email" t="new-admin-email" />
            <Inp v={newAdmin.password} set={(x) => setNewAdmin({ ...newAdmin, password: x })} ph="Password" t="new-admin-password" />
            <select value={newAdmin.role} onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })} className="bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 focus:outline-none" data-testid="new-admin-role">
              <option value="jjs_admin" className="bg-[#12332A]">JJS Admin</option>
              <option value="superadmin" className="bg-[#12332A]">Super Admin</option>
            </select>
            <button onClick={createAdmin} className="btn-gold py-3 rounded-xl" data-testid="new-admin-submit">Create</button>
          </div>
        </Modal>
      )}
    </Shell>
  );
}

function Inp({ v, set, ph, t }) {
  return <input value={v} onChange={(e) => set(e.target.value)} placeholder={ph} className="bg-black/30 gold-border rounded-xl px-3 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none" data-testid={t} />;
}

function Modal({ children, onClose, title, testid }) {
  useScrollLock(true);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="glass rounded-3xl p-6 max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl gold-text">{title}</h3>
          <button onClick={onClose} className="text-slate-400"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
